import { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

export const READER_ID = 'barcode-camera-reader';
const SAME_CODE_COOLDOWN_MS = 1400;

/** אזור סריקה רחב לברקודים חד-ממדיים (Code128) — נגזר מגודל התצוגה */
function computeQrbox(viewfinderWidth, viewfinderHeight) {
  const width = Math.max(240, Math.floor(viewfinderWidth * 0.92));
  const height = Math.max(120, Math.min(
    Math.floor(viewfinderHeight * 0.45),
    Math.floor(width * 0.4),
  ));
  return { width, height };
}

/**
 * useBarcodeCamera — סריקה רציפה ממצלמת המכשיר לתוך האלמנט READER_ID (משותף לעיצוב הישן והחדש).
 * הסטרים נשאר חי בין סריקות; paused מונע onScan בלי לסגור את המצלמה.
 */
export function useBarcodeCamera({ active, paused = false, onScan }) {
  const scannerRef = useRef(null);
  const onScanRef = useRef(onScan);
  const pausedRef = useRef(paused);
  const lastCodeRef = useRef('');
  const lastScanAtRef = useRef(0);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);

  useEffect(() => { onScanRef.current = onScan; }, [onScan]);
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (!scanner) return;
    try {
      if (scanner.isScanning) await scanner.stop();
    } catch { /* ignore */ }
    try { scanner.clear(); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!active) {
      stopScanner();
      setError('');
      setStarting(false);
      return undefined;
    }

    let cancelled = false;
    setError('');
    setStarting(true);
    lastCodeRef.current = '';
    lastScanAtRef.current = 0;

    const start = async () => {
      try {
        await new Promise((r) => setTimeout(r, 80));
        if (cancelled) return;

        const scanner = new Html5Qrcode(READER_ID, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.QR_CODE,
          ],
          verbose: false,
        });
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 15,
            qrbox: computeQrbox,
            disableFlip: true,
          },
          (decodedText) => {
            if (cancelled || pausedRef.current) return;
            const code = String(decodedText || '').trim();
            if (!code) return;

            const now = Date.now();
            if (
              code === lastCodeRef.current
              && now - lastScanAtRef.current < SAME_CODE_COOLDOWN_MS
            ) {
              return;
            }
            lastCodeRef.current = code;
            lastScanAtRef.current = now;
            onScanRef.current?.(code);
          },
          () => { /* ignore frame errors */ }
        );
        if (!cancelled) setStarting(false);
      } catch (err) {
        if (cancelled) return;
        setStarting(false);
        const msg = err?.message || String(err);
        if (/NotAllowedError|Permission|denied/i.test(msg)) {
          setError('אין הרשאה למצלמה. יש לאשר גישה למצלמה בדפדפן.');
        } else if (/NotFoundError|Requested device not found/i.test(msg)) {
          setError('לא נמצאה מצלמה במכשיר.');
        } else if (/secure|https|Secure contexts/i.test(msg)) {
          setError('סריקת מצלמה דורשת חיבור מאובטח (HTTPS).');
        } else {
          setError(msg || 'שגיאה בהפעלת המצלמה');
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      stopScanner();
    };
  }, [active, stopScanner]);

  return { error, starting };
}
